/**
 * @file Pure view helpers for the Hypervisor panel — state→pill mapping,
 *       incident detection, capacity, and rail filtering. No React, no
 *       DOM; unit-tested directly.
 * @module @labcraft/admin/ui/hypervisor/view
 *
 * The fleet read carries each machine's live lifecycle `state` (#1337) and
 * engagement `pin` (#1340); the helpers derive the state pill, the rail
 * counts/filters, the operator-action affordances (the state →
 * allowed-admin-actions table from umbrella §D), and the pinned
 * content-version display. A machine whose `state` is still `undefined`
 * (mid-drain) degrades honestly (idle pill, no incident, not counted as
 * running, no actions).
 */

import type {
  AdminMachine,
  AdminMachinePin,
  MachineLifecycleState,
} from '../../shared/hypervisor_routes.ts'

/** The `.spill--*` variant a machine state maps to. */
export type StateTone = 'ok' | 'idle' | 'warn' | 'bad'

/**
 * The operator actions a machine's state affords (umbrella §D — the state
 * → allowed-admin-actions table layered on the owner's `TRANSITIONS`).
 * `running` (live pty) → Observe + Suspend; `saved` / `paused` /
 * transitional / `terminating` / `failed` → neither. Resume is never an
 * admin action (the owner is the gate).
 *
 * @property observe - Whether the read-only pty observer may attach.
 * @property suspend - Whether operator-suspend may be issued.
 */
export type MachineAffordance = {
  observe: boolean
  suspend: boolean
}

/** The rail filter selection. */
export type MachineFilter = 'all' | 'running' | 'saved' | 'incidents'

/** Pure derivations over the machine list. */
export class HypervisorView {
  /** States that count as actively running. */
  static readonly RUNNING_STATES: ReadonlySet<MachineLifecycleState> = new Set([
    'running',
    'started',
    'resuming',
  ])

  /** Transient/working states shown as a warn pill. */
  static readonly WORKING_STATES: ReadonlySet<MachineLifecycleState> = new Set([
    'provisioning',
    'starting',
    'pausing',
    'snapshotting',
  ])

  /**
   * Map a machine's lifecycle state to a status-pill tone.
   *
   * @param state - The lifecycle state, or undefined when unknown.
   * @returns The `.spill--*` tone (`idle` when state is unknown).
   */
  static tone(state: MachineLifecycleState | undefined): StateTone {
    if (state === undefined) return 'idle'
    if (state === 'terminating') return 'bad'
    if (HypervisorView.RUNNING_STATES.has(state)) return 'ok'
    if (HypervisorView.WORKING_STATES.has(state)) return 'warn'
    return 'idle'
  }

  /**
   * Whether a machine is in an incident state (stuck terminating).
   *
   * @param machine - The machine.
   * @returns Whether it should surface as an incident.
   */
  static isIncident(machine: AdminMachine): boolean {
    return machine.state === 'terminating'
  }

  /**
   * Running / total capacity across the fleet.
   *
   * @param machines - The machine list.
   * @returns `{ running, total }`.
   */
  static capacity(machines: AdminMachine[]): {
    running: number
    total: number
  } {
    const running = machines.filter(
      m => m.state !== undefined && HypervisorView.RUNNING_STATES.has(m.state),
    ).length
    return { running, total: machines.length }
  }

  /**
   * Whether a machine has a live pty to observe (umbrella §D). Enabled
   * ONLY for `running` — the sole state whose pty is interactive (bytes
   * flow). Every other state — `saved` / transitional (`starting` /
   * `started` / `resuming` / `pausing` / `snapshotting`) / `terminating` /
   * `failed` — has no live pty, so Observe is disabled. This is the
   * affordance-level guarantee that a read-only attach NEVER cold-boots or
   * resumes a VM (a `saved` attach would implicitly resume; disabling the
   * button is the first gate, the proxy's not-running refusal the second).
   *
   * @param state - The lifecycle state, or undefined when unknown.
   * @returns Whether the Observe action is available.
   */
  static canObserve(state: MachineLifecycleState | undefined): boolean {
    return state === 'running'
  }

  /**
   * Whether a machine may be operator-suspended (umbrella §D). Enabled
   * ONLY for `running` — a suspend snapshots a live VM down to `saved`, so
   * a machine that is already `saved` / transitional / terminal has nothing
   * to reclaim.
   *
   * @param state - The lifecycle state, or undefined when unknown.
   * @returns Whether the operator-suspend action is available.
   */
  static canSuspend(state: MachineLifecycleState | undefined): boolean {
    return state === 'running'
  }

  /**
   * The operator-action affordances a machine's state permits.
   *
   * @param state - The lifecycle state, or undefined when unknown.
   * @returns The `{ observe, suspend }` affordance record.
   */
  static affordance(
    state: MachineLifecycleState | undefined,
  ): MachineAffordance {
    return {
      observe: HypervisorView.canObserve(state),
      suspend: HypervisorView.canSuspend(state),
    }
  }

  /**
   * A compact "pinned content version" display for a machine's engagement
   * pin (umbrella #1258 Part D / #1340) — the topmost layer ref (the
   * content layer welded above the base floor), or the base itself when the
   * stack is base-only. `null` when the machine carries no pin (the fake
   * tier / a pin-less session).
   *
   * @param pin - The machine's engagement pin, or undefined.
   * @returns The display version string, or `null`.
   */
  static pinVersion(pin: AdminMachinePin | undefined): string | null {
    if (!pin || pin.layers.length === 0) return null
    return pin.layers[pin.layers.length - 1]!
  }

  /**
   * Whether a machine's pin is stale — force-expired / security-expired, so
   * its next resume will clean-refresh onto current content rather than
   * restore. `false` for a live pin or a pin-less machine.
   *
   * @param pin - The machine's engagement pin, or undefined.
   * @returns Whether the pin is stale.
   */
  static pinStale(pin: AdminMachinePin | undefined): boolean {
    return pin?.stale ?? false
  }

  /**
   * Filter the machine list to the rail selection.
   *
   * @param machines - The machine list.
   * @param filter - The active rail filter.
   * @returns The matching machines.
   */
  static filter(
    machines: AdminMachine[],
    filter: MachineFilter,
  ): AdminMachine[] {
    switch (filter) {
      case 'running':
        return machines.filter(
          m =>
            m.state !== undefined && HypervisorView.RUNNING_STATES.has(m.state),
        )
      case 'saved':
        return machines.filter(m => m.state === 'saved')
      case 'incidents':
        return machines.filter(HypervisorView.isIncident)
      case 'all':
      default:
        return machines
    }
  }
}
